import { Box, Button, Card, Chip, Stack, Typography, useTheme } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import AvailabilityBadge from '@src/App/Components/Core/Badges/AvailabilityBadge/AvailabilityBadge';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Employee badge interface
 */
export interface EmployeeBadge {
    type: 'status' | 'performance' | 'skill' | 'compliance';
    label: string;
    color: 'success' | 'warning' | 'error' | 'info';
    visible: ('employee' | 'manager' | 'admin')[];
}

/**
 * Employee data interface
 */
export interface MvpEmployeeData {
    id: number;
    firstName: string;
    lastName: string;
    pictureId: string | null;
    role?: string;
    orgUnit?: string;
    location?: string;
    email?: string;
    availability?: number;
    superPower?: string;
    badges?: EmployeeBadge[];
}

/**
 * Props interface
 */
interface MvpEmployeeCardProps {
    employee: MvpEmployeeData;
    context?: 'employees' | 'projects' | 'opportunities' | 'teams';
    userRole?: 'employee' | 'manager' | 'admin';
    onAction?: (action: string, employeeId: number) => void;
}

/**
 * Get visible elements based on user role
 */
const getVisibleElements = (userRole: string) => ({
    contact: true,
    availability: ['manager', 'admin'].includes(userRole),
    performanceBadges: ['manager', 'admin'].includes(userRole),
});

/**
 * Get contextual actions based on context
 */
const getContextualActions = (context: string) => {
    const actionMap: Record<
        string,
        {
            primary: { label: string; icon: EIconCategory; action: string; color?: string };
            secondary: { label: string; icon: EIconCategory; action: string }[];
        }
    > = {
        employees: {
            primary: {
                label: 'Profil anzeigen',
                icon: EIconCategory.PROFILE,
                action: 'view-profile',
                color: 'primary',
            },
            secondary: [
                { label: 'Nachricht', icon: EIconCategory.MESSAGES, action: 'send-message' },
                { label: 'Termin', icon: EIconCategory.CALENDAR, action: 'schedule-meeting' },
            ],
        },
        projects: {
            primary: {
                label: 'Anfrage senden',
                icon: EIconCategory.MESSAGES,
                action: 'send-request',
                color: 'success',
            },
            secondary: [
                { label: 'Verfügbarkeit', icon: EIconCategory.CALENDAR, action: 'check-availability' },
                { label: 'Skills', icon: EIconCategory.SKILLS, action: 'compare-skills' },
            ],
        },
        opportunities: {
            primary: {
                label: 'Zuweisen',
                icon: EIconCategory.ASSIGNMENTS,
                action: 'assign-opportunity',
                color: 'warning',
            },
            secondary: [
                { label: 'Bewerten', icon: EIconCategory.RATINGS, action: 'rate-match' },
                { label: 'Kontakt', icon: EIconCategory.CONTACT, action: 'contact' },
            ],
        },
    };

    return actionMap[context] || actionMap.employees;
};

/**
 * Get filtered badges based on user role
 */
const getFilteredBadges = (badges: EmployeeBadge[] = [], userRole: string) => {
    return badges.filter((badge) => badge.visible.includes(userRole as 'employee' | 'manager' | 'admin'));
};

/**
 * Modern MVP Employee Card with contextual actions and role-based permissions
 */
function MvpEmployeeCard({ employee, context = 'employees', userRole = 'employee', onAction }: MvpEmployeeCardProps) {
    const theme = useTheme();
    const navigate = useNavigate();

    const visibleElements = getVisibleElements(userRole);
    const { primary: primaryAction, secondary: secondaryActions } = getContextualActions(context);
    const filteredBadges = getFilteredBadges(employee.badges, userRole);

    const handleAction = (action: string) => {
        if (onAction) {
            onAction(action, employee.id);
        } else if (action === 'view-profile') {
            navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${employee.id}`);
        }
    };

    return (
        <Card
            sx={{
                borderRadius: 3,
                p: 3,
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4],
                },
            }}
        >
            {/* Large Avatar */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <EmployeePicture
                    employee={{
                        id: employee.id,
                        firstName: employee.firstName,
                        lastName: employee.lastName,
                        pictureId: employee.pictureId,
                    }}
                    avatarSize={120}
                />
            </Box>

            {/* Name */}
            <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                {employee.firstName} {employee.lastName}
            </Typography>

            {/* Role */}
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {employee.role || 'Mitarbeiter'}
            </Typography>

            {/* Status Badges */}
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {employee.orgUnit && (
                    <Chip
                        icon={getAppIcon(EIconCategory.DEPARTMENTS)}
                        label={employee.orgUnit}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            px: 1,
                            '& .MuiChip-icon': {
                                color: theme.palette.primary.main,
                                fontSize: '16px',
                            },
                        }}
                    />
                )}

                {visibleElements.availability && employee.availability !== undefined && (
                    <AvailabilityBadge availability={employee.availability} />
                )}

                {employee.superPower && (
                    <Chip
                        icon={getAppIcon(EIconCategory.SUPERPOWERS)}
                        label={employee.superPower}
                        size="small"
                        sx={{
                            bgcolor: theme.palette.secondary.main,
                            color: theme.palette.secondary.contrastText,
                            fontWeight: 500,
                            px: 1,
                            '& .MuiChip-icon': {
                                color: theme.palette.secondary.contrastText,
                                fontSize: '16px',
                            },
                        }}
                    />
                )}
            </Box>

            {/* Contact Info */}
            {visibleElements.contact && (
                <Stack spacing={1} sx={{ mb: 3 }}>
                    {employee.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                            {getAppIcon(EIconCategory.LOCATION)}
                            <Typography variant="body2">{employee.location}</Typography>
                        </Box>
                    )}
                    {employee.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                            {getAppIcon(EIconCategory.MESSAGES)}
                            <Typography variant="body2">{employee.email}</Typography>
                        </Box>
                    )}
                </Stack>
            )}

            {/* Additional Badges */}
            {filteredBadges.length > 0 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                    {filteredBadges.map((badge, index) => (
                        <Chip key={index} label={badge.label} color={badge.color} size="small" variant="outlined" />
                    ))}
                </Stack>
            )}

            {/* Primary CTA */}
            <Button
                variant="contained"
                color={
                    (primaryAction.color as 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning') ||
                    'primary'
                }
                startIcon={getAppIcon(primaryAction.icon)}
                fullWidth
                sx={{ mb: 1 }}
                onClick={() => handleAction(primaryAction.action)}
            >
                {primaryAction.label}
            </Button>

            {/* Secondary Actions */}
            {/* Temporarily disabled - TODO: Implement message and calendar functionality
            {secondaryActions.map((action, index) => (
                <Button
                    key={index}
                    variant="outlined"
                    startIcon={getAppIcon(action.icon)}
                    fullWidth
                    sx={{ mb: 1 }}
                    onClick={() => handleAction(action.action)}
                >
                    {action.label}
                </Button>
            ))}
            */}
        </Card>
    );
}

export default MvpEmployeeCard;
